import axios from "axios";
import { createAuthAxios, handleApiError } from "../utils/apiUtils";
import { API_BASE_URL } from "../config/apiConfig";

// Endpoints for profile photo upload
const PROFILE_PHOTO_ENDPOINTS = {
  GET_UPLOAD_URL: `${API_BASE_URL}/users/upload-profile-photo`,
  UPDATE_PROFILE_PHOTO: `${API_BASE_URL}/users/update-profile-photo`,
  REMOVE_PROFILE_PHOTO: `${API_BASE_URL}/users/remove-profile-photo`,
};

/**
 * Service for handling profile photo uploads
 */
const profilePhotoService = {
  /**
   * Complete profile photo upload (3-step process)
   * @param {File} file - File to upload
   * @returns {Promise<Object>} Upload result with profilePhotoUrl
   */
  uploadProfilePhoto: async (file) => {
    try {
      // Validate file
      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "image/webp",
      ];
      const maxSize = 2 * 1024 * 1024; // 2MB

      if (!allowedTypes.includes(file.type)) {
        throw new Error("Only JPG, PNG, GIF, and WebP files are allowed");
      }

      if (file.size > maxSize) {
        throw new Error("File size must be less than 2MB");
      }

      // Step 1: Get pre-signed URL
      const uploadData = await profilePhotoService.getUploadUrl({
        filename: file.name,
        contentType: file.type,
      });

      if (!uploadData.success) {
        throw new Error(uploadData.message || "Failed to get upload URL");
      }

      // Step 2: Upload file directly to S3
      await profilePhotoService.uploadToS3(uploadData.uploadUrl, file);

      // Step 3: Update profile photo URL in database
      const updateData = await profilePhotoService.updateProfilePhoto(
        uploadData.key
      );

      if (!updateData.success) {
        throw new Error(updateData.message || "Failed to update profile photo");
      }

      return {
        success: true,
        profilePhotoUrl: updateData.profilePhotoUrl,
        user: updateData.user,
      };
    } catch (error) {
throw error;
    }
  },

  /**
   * Get pre-signed URL for uploading profile photo to S3
   * @param {Object} fileInfo - Object containing filename and contentType
   * @returns {Promise<Object>} Object containing uploadUrl and key
   */
  getUploadUrl: async (fileInfo) => {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.post(
        PROFILE_PHOTO_ENDPOINTS.GET_UPLOAD_URL,
        fileInfo
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Upload file to S3 using pre-signed URL
   * @param {string} uploadUrl - Pre-signed S3 URL
   * @param {File} file - File to upload
   * @returns {Promise<Object>} Response from S3
   */
  uploadToS3: async (uploadUrl, file) => {
    try {
      const response = await axios.put(uploadUrl, file, {
        headers: {
          "Content-Type": file.type,
        },
      });
      return response.data;
    } catch (error) {
throw error;
    }
  },

  /**
   * Update user profile with uploaded image URL
   * @param {string} key - S3 object key of the uploaded image
   * @returns {Promise<Object>} Updated user object with profilePhotoUrl
   */
  updateProfilePhoto: async (key) => {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.patch(
        PROFILE_PHOTO_ENDPOINTS.UPDATE_PROFILE_PHOTO,
        { key }
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Remove profile photo from user account
   * @returns {Promise<Object>} Updated user object with profilePhotoUrl removed
   */
  removeProfilePhoto: async () => {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.delete(
        PROFILE_PHOTO_ENDPOINTS.REMOVE_PROFILE_PHOTO
      );
      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },
};

export default profilePhotoService;
